import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import heroAI from '../assets/hero_ai_neural.png';
import aiLabImage from '../assets/students_ai_lab.png';


const aiFeatures = [
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
      </svg>
    ),
    title: 'Computer Vision',
    desc: 'Object detection, image segmentation, facial recognition, OCR, medical imaging AI systems.',
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
      </svg>
    ),
    title: 'Natural Language Processing',
    desc: 'Chatbots, sentiment analysis, text summarization, language translation, BERT/GPT fine-tuning.',
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
      </svg>
    ),
    title: 'Generative AI',
    desc: 'GANs, diffusion models, LLM fine-tuning, AI content generation, synthetic data creation.',
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/>
      </svg>
    ),
    title: 'Autonomous Systems',
    desc: 'Self-driving algorithms, drone navigation, robotic process automation, reinforcement learning.',
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 2.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/>
      </svg>
    ),
    title: 'Big Data & AI Analytics',
    desc: 'Apache Spark, Hadoop, real-time streaming analytics, AI-driven business intelligence platforms.',
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3"/>
      </svg>
    ),
    title: 'AI-Powered Web Apps',
    desc: 'Intelligent dashboards, recommendation engines, predictive UIs, AI chatbot integrations.',
  },
];

export default function AIShowcase() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="ai-showcase" className="relative overflow-hidden section-pad" style={{ background: 'linear-gradient(180deg, #F5F0E8 0%, #FAFAF5 100%)' }}>
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-royal-gold/5 -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-royal-navy/5 translate-x-1/2 translate-y-1/2 blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header */}
        <div ref={ref} className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            className="gold-badge mb-6"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
            </svg>
            Advanced AI Services
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="section-title mb-4"
          >
            Where Intelligence Meets{' '}
            <span className="text-gradient-gold">Innovation</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="section-subtitle max-w-2xl mx-auto"
          >
            We are at the frontier of artificial intelligence — delivering production-ready AI systems 
            and groundbreaking research that sets new academic benchmarks.
          </motion.p>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="royal-divider w-24 mx-auto mt-6"
          />
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left - Real AI Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative h-[420px] rounded-3xl overflow-hidden shadow-2xl group"
          >
            {/* Real AI Image */}
            <img
              src={heroAI}
              alt="AI Neural Network Visualization"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />

            {/* Dark gradient overlay for text readability */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(11,28,78,0.85) 0%, rgba(11,28,78,0.2) 50%, transparent 100%)' }}
            />

            {/* Shimmer top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />

            {/* Floating badge top-right */}
            <div className="absolute top-5 right-5">
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/30 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Live AI System
              </div>
            </div>

            {/* Floating stat pills */}
            <div className="absolute top-5 left-5 flex flex-col gap-2">
              {[
                { label: 'Accuracy', value: '99.2%' },
                { label: 'Models', value: '50+' },
              ].map((s) => (
                <div key={s.label} className="bg-royal-navy/60 backdrop-blur-sm border border-royal-gold/30 rounded-xl px-3 py-2">
                  <p className="font-playfair text-royal-gold font-bold text-lg leading-none">{s.value}</p>
                  <p className="font-inter text-white/70 text-xs">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Bottom glass card */}
            <div className="absolute bottom-5 left-5 right-5">
              <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-2xl p-4">
                <p className="font-playfair text-white font-bold text-base">Neural Network Visualization</p>
                <p className="font-inter text-white/70 text-xs mt-0.5">Multi-layer deep learning architecture — real-time inference</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="font-playfair text-3xl font-bold text-royal-navy mb-6">
              AI That Transforms Academic Research Into Real-World Impact
            </h3>
            <p className="font-inter text-slate-royal/70 leading-relaxed mb-8">
              Our AI experts and research scientists collaborate with students to build 
              publication-worthy, industry-standard artificial intelligence systems. Every project 
              is crafted with meticulous precision and cutting-edge methodology.
            </p>

            {/* Key points */}
            {[
              'State-of-the-art model architectures (Transformers, CNNs, GNNs)',
              'Real datasets & real evaluation metrics (SOTA benchmarks)',
              'Publishable code with complete documentation',
              'Live demo deployments for viva & presentations',
            ].map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-start gap-3 mb-4"
              >
                <div className="w-5 h-5 rounded-full bg-royal-gold flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-inter text-sm text-slate-royal/80">{point}</p>
              </motion.div>
            ))}

            <motion.a
              href="#contact"
              whileHover={{ scale: 1.03 }}
              className="btn-gold inline-flex items-center gap-2 mt-6 text-sm"
            >
              Start AI Project
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </motion.div>
        </div>

        {/* AI Feature Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {aiFeatures.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="gradient-border bg-white rounded-2xl p-6 hover:shadow-gold transition-all duration-300 group hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-royal-navy/5 flex items-center justify-center mb-4 group-hover:bg-royal-gold/10 transition-colors duration-300 text-royal-navy group-hover:text-royal-gold">
                {feature.icon}
              </div>
              <h4 className="font-playfair font-bold text-royal-navy text-lg mb-2">{feature.title}</h4>
              <p className="font-inter text-sm text-slate-royal/70 leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
